import { render } from 'solid-js/web';
import { A, Navigate, Route, Router, useNavigate } from '@solidjs/router';
import { JSX, Match, Show, Switch, createSignal, onMount } from 'solid-js';
import './index.css';
import { DEV_LOGIN, completeLogin, devLogin, login, loginConfigured, logout, session, whoAmI } from './auth';
import { ApiError } from './api';
import { isOperator, me } from './me';
import Customers from './pages/Customers';
import CustomerPage from './pages/CustomerPage';
import ZonePage from './pages/ZonePage';
import Library from './pages/Library';
import Partners from './pages/Partners';
import Reports from './pages/Reports';
import Radio from './pages/Radio';
import StationPage from './pages/StationPage';

const roleLabel: Record<string, string> = { operator: 'Operator', partner_admin: 'Partner admin', customer_admin: 'Customer admin', customer_staff: 'Staff' };

function Brand() {
  return (
    <A href="/" class="flex items-center gap-2.5 no-underline">
      <img src="/mark.png" alt="" class="h-7 w-10 object-contain" />
      <span class="wordmark text-xl text-static">scai.fm</span>
      <span class="wordmark text-xl font-semibold text-amber">Zones</span>
    </A>
  );
}

function Layout(props: { children?: JSX.Element }) {
  const link = 'block rounded-full px-4 py-2 text-[15px] font-semibold text-lavender hover:bg-night-2 hover:text-static';
  const active = 'bg-night-3 text-static';
  return (
    <div class="flex min-h-screen flex-col md:flex-row">
      <aside class="flex flex-col gap-6 bg-night p-5 md:w-64 md:flex-none">
        <Brand />
        <nav aria-label="Admin" class="flex flex-row flex-wrap gap-1 md:flex-col">
          <Show when={isOperator() || me()?.bindings.some((b) => b.role === 'partner_admin')}>
            <A href="/partners" class={link} activeClass={active}>Partners</A>
          </Show>
          <A href="/customers" class={link} activeClass={active}>Customers</A>
          <A href="/library" class={link} activeClass={active}>Music library</A>
          <Show when={isOperator()}>
            <A href="/reports" class={link} activeClass={active}>Play reports</A>
            <A href="/radio" class={link} activeClass={active}>Radio</A>
          </Show>
        </nav>
        <div class="mt-auto hidden flex-col gap-1 border-t border-night-3 pt-4 text-sm md:flex">
          <span class="text-mist">Signed in as</span>
          <span class="font-semibold text-static">{me()?.name || me()?.email || me()?.sub}</span>
          <span class="mono text-mist">{me()?.bindings.map((b) => roleLabel[b.role]).join(' · ')}</span>
          <button class="btn btn-sm mt-2 border-night-3 bg-night-2 text-static hover:bg-night-3" onClick={() => logout()}>Sign out</button>
        </div>
      </aside>
      <main class="min-w-0 flex-1 p-4 sm:p-8">{props.children}</main>
    </div>
  );
}

function Login() {
  const [tok, setTok] = createSignal('');
  return (
    <div class="flex min-h-screen items-center justify-center bg-night p-6">
      <div class="w-full max-w-md rounded-card bg-night-2 p-8 text-static">
        <Brand />
        <p class="mt-6 text-lavender">License-free music for your business.</p>
        <Show when={loginConfigured()} fallback={<p class="mt-6 text-sm text-mist">Sign-in is not configured yet (ScaiKey client pending).</p>}>
          <button class="btn btn-primary mt-6 w-full" onClick={() => login()}>Sign in</button>
        </Show>
        <p class="mt-4 text-center text-sm text-mist">No account yet? <a class="link text-lavender" href="https://scai.fm/#invite">Request an invitation</a></p>
        <Show when={DEV_LOGIN}>
          <form class="mt-6 flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); devLogin(tok()); }}>
            <label class="text-sm text-mist" for="devtok">Development token (scaifm dev-token)</label>
            <input id="devtok" class="input input-bordered mono bg-night text-static" value={tok()} onInput={(e) => setTok(e.currentTarget.value)} />
            <button class="btn btn-secondary border-night-3" type="submit">Use token</button>
          </form>
        </Show>
      </div>
    </div>
  );
}

function Callback() {
  const nav = useNavigate();
  const [err, setErr] = createSignal('');
  onMount(async () => {
    try { nav(await completeLogin(), { replace: true }); } catch (e: any) { setErr(e.message ?? String(e)); }
  });
  return <div class="p-8"><Show when={err()} fallback="Signing in…"><p class="text-error">{err()}</p><A href="/" class="link">Back</A></Show></div>;
}

function Gate(props: { children?: JSX.Element }) {
  return (
    <Show when={session()} fallback={<Login />}>
      <Switch>
        <Match when={me.error}>
          <div class="flex min-h-screen items-center justify-center p-6">
            <div class="card-z max-w-md">
              <h1 class="wordmark text-2xl">No access yet</h1>
              <p class="mt-2 text-ink-2">
                {me.error instanceof ApiError && me.error.status === 403
                  ? 'Your account has no Scai.FM role. Ask your partner or ScaiLabs to invite you with this e-mail address.'
                  : `Could not load your account: ${me.error?.message}`}
              </p>
              <Show when={me.error instanceof ApiError && me.error.status === 403}>
                <dl class="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt class="text-ink-3">E-mail</dt><dd class="break-all">{whoAmI().email ?? '—'}</dd>
                  <dt class="text-ink-3">Account id</dt><dd class="mono break-all">{whoAmI().sub ?? '—'}</dd>
                </dl>
              </Show>
              <Show when={me.error instanceof ApiError && me.error.status === 403}>
                <p class="mt-3 text-sm text-ink-2">New to scai.fm? <a class="link" href="https://scai.fm/#invite">Request an invitation</a> with this e-mail address.</p>
              </Show>
              <button class="btn btn-secondary mt-4" onClick={() => logout()}>Sign out</button>
            </div>
          </div>
        </Match>
        <Match when={me()}><Layout>{props.children}</Layout></Match>
      </Switch>
    </Show>
  );
}

render(() => (
  <Router>
    <Route path="/auth/callback" component={Callback} />
    <Route path="/" component={Gate}>
      <Route path="/" component={() => <Navigate href="/customers" />} />
      <Route path="/partners" component={Partners} />
      <Route path="/customers" component={Customers} />
      <Route path="/customers/:id" component={CustomerPage} />
      <Route path="/zones/:id" component={ZonePage} />
      <Route path="/library" component={Library} />
      <Route path="/reports" component={Reports} />
      <Route path="/radio" component={Radio} />
      <Route path="/radio/:id" component={StationPage} />
    </Route>
  </Router>
), document.getElementById('root')!);
